// Phase 10 — ActivityFeed (expanded).
//
// Renders the activity log with severity indicators, event type pills,
// market/signal context, confidence scores, duration, and tags.
// Terminal-grade data density with color-coded severity.

import type { CSSProperties } from "react";
import type { ActivityLogEntry, UseActivityLogState } from "../../boards/useBoardItems.ts";
import * as s from "./board.styles.ts";

// ─── Helpers ───────────────────────────────────────────────────

function fmtTs(raw: string): string {
  if (raw.length === 0) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = d.getUTCDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const hr = d.getUTCHours().toString().padStart(2, "0");
  const min = d.getUTCMinutes().toString().padStart(2, "0");
  return `${day} ${mon} ${hr}:${min}`;
}

const ACTION_LABELS: Record<string, string> = {
  created: "Created",
  status_change: "Status",
  queued_for_approval: "Approval",
  approved: "Approved",
  rejected: "Rejected",
  note_added: "Note",
  signal_created: "Signal",
  agent_reasoning: "Agent",
  debate_outcome: "Debate",
  user_execution: "Executed",
  monitoring_event: "Monitor",
  chain_update: "Chain",
  risk_intervention: "Risk",
  learning_note: "Learned",
};

const SEVERITY_COLORS: Record<string, { bg: string; fg: string }> = {
  debug:    { bg: "var(--sz-surface-recessed)", fg: "var(--sz-text-muted)" },
  info:     { bg: "var(--sz-surface-recessed)", fg: "var(--sz-text-secondary)" },
  warning:  { bg: "var(--sz-amber-dim)", fg: "var(--sz-amber)" },
  error:    { bg: "var(--sz-red-dim)", fg: "var(--sz-red)" },
  critical: { bg: "var(--sz-red)", fg: "var(--sz-text-inverse)" },
};

function severityStyle(severity: string): CSSProperties {
  const c = SEVERITY_COLORS[severity] ?? SEVERITY_COLORS["info"]!;
  return { ...s.activitySeverity, background: c!.bg, color: c!.fg };
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtConfidence(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

// ─── Component ─────────────────────────────────────────────────

export interface ActivityFeedProps {
  state: UseActivityLogState;
}

export function ActivityFeed({ state }: ActivityFeedProps): JSX.Element | null {
  const { entries, loading, error } = state;

  if (loading) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-3)" }}>
      <div style={s.boardHeader}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={s.boardTitle}>Activity</span>
          <span style={s.boardSubtitle}>System-wide action log</span>
        </div>
        <span style={s.boardCount}>{String(entries.length)} events</span>
      </div>

      {error !== null ? <span style={s.emptyNote}>{error}</span> : null}

      {entries.length === 0 && error === null ? (
        <div style={s.emptyState}>
          <span style={s.emptyTitle}>No Activity</span>
          <span style={s.emptyNote}>
            Actions across all boards will be recorded here.
          </span>
        </div>
      ) : (
        <div style={s.activityRoot}>
          {entries.map((entry) => (
            <div key={entry.id} style={s.activityEntry}>
              <span style={s.activityTimestamp}>{fmtTs(entry.created_at)}</span>
              <span style={severityStyle(entry.severity)}>{entry.severity}</span>
              <span style={s.activityAction}>
                {ACTION_LABELS[entry.action] ?? entry.action}
              </span>
              <span style={s.activityActor}>{entry.actor}</span>
              {entry.summary !== null ? (
                <span style={s.activitySummary}>{entry.summary}</span>
              ) : null}
              {(entry.market_type !== null || entry.event_type !== null ||
                entry.confidence !== null || entry.duration_ms !== null ||
                entry.tags.length > 0) ? (
                <div style={s.activityMeta}>
                  {entry.event_type !== null ? (
                    <span style={s.activityTag}>{entry.event_type}</span>
                  ) : null}
                  {entry.market_type !== null ? (
                    <span style={s.activityTag}>{entry.market_type}</span>
                  ) : null}
                  {entry.signal_type !== null ? (
                    <span style={s.activityTag}>{entry.signal_type}</span>
                  ) : null}
                  {entry.confidence !== null ? (
                    <span style={s.activityTag}>
                      conf {fmtConfidence(entry.confidence)}
                    </span>
                  ) : null}
                  {entry.duration_ms !== null ? (
                    <span style={s.activityTag}>
                      {fmtDuration(entry.duration_ms)}
                    </span>
                  ) : null}
                  {entry.tags.map((tag) => (
                    <span key={tag} style={s.activityTag}>{tag}</span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
